import React, { useEffect, useState, useRef } from "react";
import { IoSearch } from "react-icons/io5";
import { HiX } from "react-icons/hi";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { toast } from "react-toastify";
import UserService from "../../../../services/UserService";
import { colors } from "../../../config/colors";

const ListAdmins = () => {
  const [adminList, setAdminList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchVal, setSearchVal] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [toggling, setToggling] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addName, setAddName] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addPassword, setAddPassword] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [showAddPassword, setShowAddPassword] = useState(false);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await UserService.getUsers({
        page: 1,
        limit: 100,
        search: searchVal,
        role: "ADMIN",
      });
      if (response.data?.status === "success") {
        setAdminList(response.data.data?.users || []);
      } else {
        throw new Error("Failed to fetch admins");
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
      toast.error("Failed to fetch admins");
      setAdminList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, [searchVal]);

  const currentUserId = JSON.parse(localStorage.getItem("userDetails") || "{}")?.user?._id;

  const handleToggle = async (admin) => {
    if (admin._id === currentUserId) {
      toast.error("You cannot deactivate yourself");
      return;
    }
    const isActive = admin.isActive !== false && !admin.isDeactivated;
    setToggling(admin._id);
    try {
      if (isActive) {
        await UserService.deactivateUser(admin._id);
        toast.success("Admin deactivated");
      } else {
        await UserService.activateUser(admin._id);
        toast.success("Admin activated");
      }
      fetchAdmins();
    } catch (error) {
      console.error("Error toggling admin:", error);
      toast.error(error.response?.data?.message || "Failed to update admin");
    } finally {
      setToggling(null);
    }
  };

  const searchRef = useRef(null);
  const handleSearch = (e) => {
    const v = e.target.value;
    setInputValue(v);
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => setSearchVal(v), 400);
  };
  useEffect(() => () => searchRef.current && clearTimeout(searchRef.current), []);

  const handleAddAdmin = async () => {
    if (!addName.trim() || !addEmail.trim() || !addPassword) {
      toast.error("Name, email and password are required");
      return;
    }
    if (addPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setAddLoading(true);
    try {
      await UserService.createUser({
        name: addName.trim(),
        email: addEmail.trim(),
        password: addPassword,
        role: "ADMIN",
      });
      toast.success("Admin added");
      setShowAddModal(false);
      setAddName("");
      setAddEmail("");
      setAddPassword("");
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add admin");
    } finally {
      setAddLoading(false);
    }
  };

  return (
    <div className="h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-white to-accent-light/10 p-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-primary-headings mb-2">Manage Admins</h2>
        <p className="text-sm text-primary-light mb-6">View and manage organisation administrators</p>
      </div>

      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6 flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <IoSearch className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={inputValue}
            onChange={handleSearch}
            className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent text-sm"
            placeholder="Search admins..."
          />
        </div>
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2.5 text-sm font-medium text-white rounded-lg shrink-0"
          style={{ backgroundColor: colors.primary.headingsBody }}
        >
          Add Admin
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-gray-300 rounded-full animate-spin" style={{ borderTopColor: colors.primary.headingsBody }} />
          </div>
        ) : adminList.length === 0 ? (
          <div className="py-16 text-center text-gray-500">No admins found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-primary-headings uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {adminList.map((admin) => {
                  const isActive = admin.isActive !== false && !admin.isDeactivated;
                  return (
                    <tr key={admin._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{admin.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{admin.email}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
                            }`}
                        >
                          {isActive ? "Active" : "Deactivated"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleToggle(admin)}
                          disabled={toggling === admin._id || admin._id === currentUserId}
                          className={`px-4 py-2 text-sm font-medium rounded-lg ${isActive
                              ? "bg-red-50 text-red-700 hover:bg-red-100"
                              : "bg-green-50 text-green-700 hover:bg-green-100"
                            } disabled:opacity-50`}
                        >
                          {toggling === admin._id ? (
                            <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          ) : admin._id === currentUserId ? (
                            "You"
                          ) : (
                            isActive ? "Deactivate" : "Activate"
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => !addLoading && setShowAddModal(false)} />
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-primary-headings">Add Admin</h3>
              <button type="button" onClick={() => !addLoading && setShowAddModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <HiX size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  placeholder="Full name"
                  disabled={addLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  placeholder="Email"
                  disabled={addLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showAddPassword ? 'text' : 'password'}
                    value={addPassword}
                    onChange={(e) => setAddPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-200 rounded-lg text-sm"
                    placeholder="Min 8 characters"
                    disabled={addLoading}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={() => setShowAddPassword((v) => !v)}
                  >
                    {showAddPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => !addLoading && setShowAddModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={addLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddAdmin}
                disabled={addLoading}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50"
                style={{ backgroundColor: colors.primary.headingsBody }}
              >
                {addLoading ? "Adding…" : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListAdmins;
